import { Badge, Button, Card, EmptyState, ListRow, LoadingState, PageLayout } from '@orbind/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api.ts'
import { useSession } from '../lib/session.tsx'
import { StatusBadge, dueLabel } from '../components/badges.tsx'
import { NOME_DO_SISTEMA, isDesktop, sistemaOperacional, urlDeDownload } from '../lib/platform.ts'
import { useCompacto } from '../shell/compacto.ts'
import './home.css'

const CHAVE_DISPENSA_DESKTOP = 'orbind:home-desktop-dispensado'

/**
 * Convite para baixar o Orbind Desktop — so aparece no NAVEGADOR (no app
 * instalado nao faz sentido) e some quando o usuario dispensa.
 */
function ConviteDesktop() {
  const [dispensado, setDispensado] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(CHAVE_DISPENSA_DESKTOP) === '1',
  )
  const so = sistemaOperacional()
  // no app instalado, em mobile ou em SO sem instalador, nao convida
  if (isDesktop() || dispensado || so === 'outro') return null

  return (
    <section className="home__desktop" aria-label="Orbind Desktop">
      <span className="home__desktop-icone" aria-hidden>
        <i className="fas fa-desktop" />
      </span>
      <div className="home__desktop-txt">
        <strong className="home__desktop-tit">Trabalhe melhor com o Orbind Desktop</strong>
        <span className="home__desktop-sub">
          Aplicativo para {NOME_DO_SISTEMA[so]}: abre direto na sua área de trabalho, sem depender de aba do navegador,
          e se atualiza sozinho.
        </span>
      </div>
      <Button variant="primary" icon="fas fa-download" onClick={() => window.open(urlDeDownload(so), '_blank')}>
        Baixar para {NOME_DO_SISTEMA[so]}
      </Button>
      <button
        type="button"
        className="home__desktop-fechar"
        aria-label="Dispensar o convite do Orbind Desktop"
        title="Dispensar"
        onClick={() => {
          window.localStorage.setItem(CHAVE_DISPENSA_DESKTOP, '1')
          setDispensado(true)
        }}
      >
        <i className="fas fa-times" aria-hidden />
      </button>
    </section>
  )
}

/**
 * HOME-01 — Início (spec 10.2).
 *
 * Prioriza o que exige acao. Os KPIs ficam numa faixa compacta no topo em vez
 * de dominarem a tela, e cada card leva para o contexto ja filtrado.
 */
export function HomePage() {
  const user = useSession()
  const navigate = useNavigate()
  /*
    Celular e tablet (SPEC-20261007-0909-layout-mobile-barra-e-inicio): a mesma
    tela, arrumada para o polegar — indicadores lado a lado, os três quadros
    empilhados com linhas mais altas, e o "novo" num botão flutuante acima da
    barra de abas, em vez de no canto do cabeçalho.
  */
  const movel = useCompacto()

  // so o que esta ABERTO: item concluido nao e prioridade de hoje
  const mine = useQuery({
    queryKey: ['work-items', 'mine', 'abertos'],
    queryFn: async () => (await api['work-items'].$get({ query: { scope: 'mine', openOnly: '1', limit: 10 } })).json(),
  })

  // atrasados DO USUARIO (responsavel) — nao os do workspace inteiro, senao
  // o painel nao bate com as Prioridades de hoje
  const overdue = useQuery({
    queryKey: ['work-items', 'overdue', user.id],
    queryFn: async () => (await api['work-items'].$get({ query: { scope: 'overdue', assigneeId: user.id, limit: 5 } })).json(),
  })

  // acompanhamento: itens em que sou MEMBRO mas nao o responsavel
  const acompanhando = useQuery({
    queryKey: ['work-items', 'member'],
    queryFn: async () => (await api['work-items'].$get({ query: { scope: 'member', limit: 5 } })).json(),
  })

  const today = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full' }).format(new Date())

  return (
    <PageLayout
      className={movel ? 'home home--movel' : 'home'}
      title={`Olá, ${user.name.split(' ')[0]}`}
      subtitle={today}
      actions={
        movel ? undefined : (
          <Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/work-items/new')}>
            Novo Item de Trabalho
          </Button>
        )
      }
    >
      {/* no celular e no tablet não há Orbind Desktop para instalar */}
      {movel ? null : <ConviteDesktop />}
      {movel ? (
        <button
          type="button"
          className="home__fab"
          aria-label="Novo Item de Trabalho"
          onClick={() => navigate('/work-items/new')}
        >
          <i className="fas fa-plus" aria-hidden />
          Novo Item
        </button>
      ) : null}

      <section className="home__kpis" aria-label="Meus indicadores">
        <Kpi label="Atribuídos a mim" value={mine.data?.total} onClick={() => navigate('/work-items?scope=mine')} />
        <Kpi
          label="Atrasados"
          value={overdue.data?.total}
          tone="danger"
          onClick={() => navigate('/work-items?scope=overdue')}
        />
      </section>

      <div className="home__columns">
        <Card variant="flush">
          <header className="home__card-header">
            <h2>Prioridades de hoje</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/work-items?scope=mine')}>
              Ver todos
            </Button>
          </header>
          {mine.isPending ? (
            <div style={{ padding: 16 }}>
              <LoadingState rows={4} />
            </div>
          ) : mine.data && mine.data.items.length > 0 ? (
            mine.data.items.map((item) => (
              <ListRow
                key={item.id}
                code={item.code}
                title={item.title}
                meta={
                  <>
                    {dueLabel(item.dueAt, item.status.isTerminal)}
                    <StatusBadge status={item.status} />
                  </>
                }
                onClick={() => navigate(`/work-items/${item.id}`)}
              />
            ))
          ) : (
            <EmptyState
              icon="fas fa-check-circle"
              title="Nada pendente para você"
              description="Você não tem Itens de Trabalho atribuídos no momento."
              action={<Button onClick={() => navigate('/work-items')}>Ver todos os Itens de Trabalho</Button>}
            />
          )}
        </Card>

        <div className="home__coluna-dir">
          <Card variant="flush">
            <header className="home__card-header">
              <h2>Atrasados</h2>
              <Badge tone="danger" icon="fas fa-exclamation-triangle">
                {overdue.data?.total ?? 0}
              </Badge>
            </header>
            {overdue.isPending ? (
              <div style={{ padding: 16 }}>
                <LoadingState rows={3} />
              </div>
            ) : overdue.data && overdue.data.items.length > 0 ? (
              overdue.data.items.map((item) => (
                <ListRow
                  key={item.id}
                  code={item.code}
                  title={item.title}
                  meta={dueLabel(item.dueAt, item.status.isTerminal)}
                  onClick={() => navigate(`/work-items/${item.id}`)}
                />
              ))
            ) : (
              <EmptyState
                icon="fas fa-thumbs-up"
                title="Nenhum item atrasado"
                description="Seus Itens de Trabalho estão dentro do prazo."
              />
            )}
          </Card>

          <Card variant="flush">
            <header className="home__card-header">
              <h2>Acompanhamento</h2>
              <Badge tone="neutral" icon="fas fa-user-friends">
                {acompanhando.data?.total ?? 0}
              </Badge>
            </header>
            {acompanhando.isPending ? (
              <div style={{ padding: 16 }}>
                <LoadingState rows={3} />
              </div>
            ) : acompanhando.data && acompanhando.data.items.length > 0 ? (
              acompanhando.data.items.map((item) => (
                <ListRow
                  key={item.id}
                  code={item.code}
                  title={item.title}
                  meta={
                    <>
                      {dueLabel(item.dueAt, item.status.isTerminal)}
                      <StatusBadge status={item.status} />
                    </>
                  }
                  onClick={() => navigate(`/work-items/${item.id}`)}
                />
              ))
            ) : (
              <EmptyState
                icon="fas fa-user-friends"
                title="Nada em acompanhamento"
                description="Itens em que você é membro (sem ser o responsável) aparecem aqui."
              />
            )}
          </Card>
        </div>
      </div>
    </PageLayout>
  )
}

function Kpi({
  label,
  value,
  tone = 'neutral',
  onClick,
}: {
  label: string
  value: number | undefined
  tone?: 'neutral' | 'warning' | 'danger'
  onClick: () => void
}) {
  return (
    <button type="button" className={`home__kpi home__kpi--${tone}`} onClick={onClick}>
      <span className="home__kpi-value">{value ?? '—'}</span>
      <span className="home__kpi-label">{label}</span>
    </button>
  )
}
